import React from 'react';
import PropTypes from 'prop-types';
import requiredIf from 'react-required-if';
import normalizeUrl from 'normalize-url';
import request from 'superagent';
import { Link, Body } from '../../styled components/Typography';
import SVGIcon from '../SVGIcon/SVGIcon';
import {
  StyledCheckboxList,
  StyledCheckboxListItem,
  StyledContactInfoLabel,
  StyledContactLinkName,
  StyledObjectName,
  StyledObjectRole,
  StyledContainerBreakWords,
  StyledAttachmentDetails,
  StyledCustomDataLabel,
  StyledCustomDataDescription
} from './CustomDataReadOnlyField.style';
import { sessionStorageItemNameAppJWT } from '../../libs/sessionStorageItemNames';
import {
  isValidUrl,
  isValidEmail,
  isValidPhone,
  isValidFax
} from '../../libs/contactDataUtilities';
import { YPDateTimeObjectPropType } from '../../libs/dateTimeUtilities';
import FormatSettingsContext from '../FormatSettingsContext';
import RequestHandler from '../../libs/requestHandling';

/**
 * Use the `CustomDataReadOnlyField` to print the custom data read-only fields
 *
 * **A field exists out of*e
 *
 * * label (optional)
 * * value
 * * description (optional)
 *
 * Use the `emptyValue` property when there is no value.
 *
 * **The value has multiple possible types to be rendered**
 *
 * * string
 * * multiline string
 * * list of read-only checkbox options
 * * contact
 * * resource
 * * attachment
 */


export default class CustomDataReadOnlyField extends React.Component {
  renderCheckboxOptions() {
    const { value } = this.props;
    return (
      <StyledCheckboxList>
        {value.map((option) => {
          return (
            <StyledCheckboxListItem key={option.label}>
              {option.checked && <SVGIcon icon="check" tw="text-blue-base" />}
              {!option.checked && <SVGIcon icon="close" tw="text-gray-lighten-30" />}
              <Body>{option.label}</Body>
            </StyledCheckboxListItem>
          );
        })}
      </StyledCheckboxList>
    );
  }

  renderContactInfoValueEmail(emailValue) {
    const strippedWhitespaceValue = emailValue.split(' ').join('');
    return (
      <Link title={`E-mail ${emailValue}`} href={`mailto:${strippedWhitespaceValue}`}>
        {emailValue}
      </Link>
    );
  }

  renderContactInfoValueWebsite(websiteUrl) {
    return (
      <Link
        title={`Visit ${websiteUrl}`}
        href={normalizeUrl(websiteUrl)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {websiteUrl}
      </Link>
    );
  }

  renderContactInfoValuePhone(phone) {
    const strippedWhitespaceValue = phone.split(' ').join('');
    return (
      <Link title={`Call ${phone}`} href={`tel:${strippedWhitespaceValue}`}>
        {phone}
      </Link>
    );
  }

  renderContactInfoValueFax(fax) {
    const strippedWhitespaceValue = fax.split(' ').join('');
    return (
      <Link title={`Fax ${fax}`} href={`fax:${strippedWhitespaceValue}`}>
        {fax}
      </Link>
    );
  }

  renderContactInfoValueString(contactInfoItem) {
    return <span>{contactInfoItem}</span>;
  }

  renderContactInfoValue(contactInfoItem) {
    const { contactType, contactValue } = contactInfoItem;
    const strippedWhitespaceValue = contactValue.split(' ').join('');
    if (contactType === 'E-mail' && isValidEmail(contactValue))
      return this.renderContactInfoValueEmail(contactValue);
    if (contactType === 'Website' && isValidUrl(contactValue))
      return this.renderContactInfoValueWebsite(contactValue);
    if (contactType === 'Telephone' || contactType === 'Mobile')
      if (isValidPhone(strippedWhitespaceValue))
        return this.renderContactInfoValuePhone(contactValue);
    if (contactType === 'Fax' && isValidFax(contactValue))
      return this.renderContactInfoValueFax(contactValue);

    return this.renderContactInfoValueString(contactValue);
  }

  renderContact() {
    const { value } = this.props;
    const { name, role, contactLinkName, contactInfo } = value;
    return (
      <>
        <div data-test-type="contactNameAndRole">
          <StyledObjectName>{name}</StyledObjectName>
          {role && <StyledObjectRole>({role})</StyledObjectRole>}
        </div>
        {contactLinkName && (
          <StyledContactLinkName data-test-type="contactLinkName">
            {contactLinkName}
          </StyledContactLinkName>
        )}
        {contactInfo && (
          <div data-test-type="contactInfo">
            {contactInfo.map((item) => (
              <div
                key={item.contactValue}
                tw="flex"
                data-test-contactinfo-type={item.contactType}
              >
                <StyledContactInfoLabel data-test-contactinfo="label">
                  {`${item.contactType}: `}
                </StyledContactInfoLabel>
                <StyledContainerBreakWords data-test-contactinfo="value">
                  {this.renderContactInfoValue(item)}
                </StyledContainerBreakWords>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  renderResource() {
    const { value } = this.props;
    const { name, role } = value;
    return (
      <div>
        <StyledObjectName>{name}</StyledObjectName>
        {role && <StyledObjectRole>{role}</StyledObjectRole>}
      </div>
    );
  }

  renderAttachment() {
    const { value } = this.props;
    const { dateFormatSetting, timeFormatSetting } = this.context;
    const { url, extension, userName } = value;
    const dateStringOrNull = value.printCreationDate(
      dateFormatSetting,
      timeFormatSetting
    );
    const attachmentName = value.printName();
    return (
      <>
        {value.isDocument() && (
          <Link
            onClick={() => {
              const jwt = sessionStorage.getItem(sessionStorageItemNameAppJWT);
              const windowReference = window.open();
              let documentUrl = url;
              new RequestHandler().get('/documentToken', {}, (res, error) => {
                if (!error && res.body.token) {
                  documentUrl += `?documentToken=${res.body.token}`;
                }
                /* Sometimes the windowReference can be null when it failed to open. In that case, we retry to open a window with the url.
                   We cannot always do this, because Safari blocks a window.open that is performed in an asynchronous call, like this req.end  */
                if (windowReference === null) window.open(documentUrl);
                else windowReference.location.href = documentUrl;
              });
            }}
          >
            {attachmentName}
          </Link>
        )}
        {!value.isDocument() && (
          <Link
            href={url || attachmentName}
            title={`Open ${attachmentName || url}`}
            target="_blank"
            rel="noreferrer noopener"
          >
            {url}
          </Link>
        )}
        <StyledAttachmentDetails data-test-type="attachmentDetails">
          {extension && <span tw="uppercase">{extension}</span>}
          {extension && dateStringOrNull && <span>, </span>}
          {dateStringOrNull && <span>{dateStringOrNull}</span>}
          {(extension || dateStringOrNull) && userName && <span>, </span>}
          {userName && <span>{userName}</span>}
        </StyledAttachmentDetails>
      </>
    );
  }

  renderString() {
    const { value } = this.props;
    const { multiline, string } = value;
    if (multiline) return <p tw="whitespace-pre-line">{string}</p>;
    return <p>{string}</p>;
  }

  renderEmptyValue() {
    const { emptyValue } = this.props;
    return <Body $color="gray dark">{emptyValue}</Body>;
  }

  renderTypeSpecificValue() {
    const { value, emptyValue, type } = this.props;
    if (emptyValue) return this.renderEmptyValue();
    switch (type) {
      case 'string':
        return this.renderString();
      case 'contact':
        return this.renderContact();
      case 'resource':
        return this.renderResource();
      case 'attachment':
        return this.renderAttachment();
      case 'checkbox':
        return this.renderCheckboxOptions();
      default:
        throw new Error(
          `CustomDataReadOnlyField: could not render value for type ${type}`
        );
    }
  }

  render() {
    const { label, description } = this.props;
    return (
      <StyledContainerBreakWords data-test-type="customDataField">
        {label && (
          <div tw="mb-4">
            <StyledCustomDataLabel>{label}</StyledCustomDataLabel>
          </div>
        )}
        <div data-test-type="customDataFieldValue">
          <Body as="div">
            {this.renderTypeSpecificValue()}
            {description && (
              <StyledCustomDataDescription>{description}</StyledCustomDataDescription>
            )}
          </Body>
        </div>
      </StyledContainerBreakWords>
    );
  }
}

CustomDataReadOnlyField.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  type: PropTypes.oneOf(['string', 'contact', 'resource', 'attachment', 'checkbox'])
    .isRequired,
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({
      /* Text */
      string: requiredIf(PropTypes.string, (value) => value.type === 'string'),
      multiline: PropTypes.bool,
      /* Checkbox */
      options: requiredIf(
        PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            checked: PropTypes.bool
          })
        ),
        (value) => value.type === 'checkbox'
      ),
      name: requiredIf(
        PropTypes.string,
        (value) => value.type === 'resource' || value.type === 'contact'
      ),
      /* Role on resource booking or contact booking */
      role: PropTypes.string,
      /* Attachment */
      url: PropTypes.string,
      extension: PropTypes.string,
      date: YPDateTimeObjectPropType,
      userName: PropTypes.string,
      /* Contact */
      contactLinkName: PropTypes.string,
      contactInfo: PropTypes.arrayOf(
        PropTypes.shape({
          contactType: PropTypes.string,
          contactValue: PropTypes.string
        })
      )
    })
  ]),
  emptyValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};

CustomDataReadOnlyField.contextType = FormatSettingsContext;

CustomDataReadOnlyField.defaultProps = {
  label: false,
  description: false,
  emptyValue: false,
  value: null
};
